import { register } from 'swiper/element/bundle'
import { reanimateImages } from './utils'
// Register Swiper custom elements
register()
export class SwiperInit {
  constructor() {
    this.initializedSwipers = new WeakSet()
    this.observer = null

    // Initialize swipers
    this.initializeSwipers()

    // Re-init for Shopify editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        this.initializeSwipers()
      })
      document.addEventListener('shopify:section:unload', (event) => {
        this.cleanupSection(event)
      })
    }
  }

  cleanupSection(event) {
    const sectionId = event?.detail?.sectionId
    if (!sectionId) return

    const section = document.getElementById(`shopify-section-${sectionId}`)
    if (!section) return

    const swipers = section.querySelectorAll('swiper-container')
    swipers.forEach(swiperEl => {
      if (swiperEl._resizeHandler) {
        window.removeEventListener('resize', swiperEl._resizeHandler)
        swiperEl._resizeHandler = null
      }
      if (swiperEl.swiper) {
        swiperEl.swiper.destroy(true, true)
      }
    })
  }

  initializeSwipers() {
    const swipers = document.querySelectorAll('swiper-container')

    // In Shopify design mode, initialize all swipers immediately
    if (Shopify.designMode) {
      swipers.forEach(swiperEl => this.initSwiperElement(swiperEl))
      return
    }

    // Setup IntersectionObserver for lazy loading
    if (!this.observer && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.initSwiperElement(entry.target)
            this.observer.unobserve(entry.target)
          }
        })
      }, {
        rootMargin: '200px', // Start loading 200px before entering viewport
        threshold: 0.01
      })
    }
    
    // Observe all uninitialized swipers
    swipers.forEach(swiperEl => {
      if (!this.initializedSwipers.has(swiperEl)) {
        if (Shopify.designMode || (this.observer && window.getComputedStyle(swiperEl).display !== 'none')) {
          this.observer?.observe(swiperEl)
        }
      }
    })
  }

  initSwiperElement(swiperEl) {
    if (this.initializedSwipers.has(swiperEl)) return
    this.initializedSwipers.add(swiperEl)

    // Get basic settings from data attributes
    const hasTextPagination = swiperEl.dataset.textPagination === 'true'

    // Get space between settings from data attributes
    const spaceBetweenMobile = Number(swiperEl.dataset.spaceBetweenMobile) || 0
    const spaceBetweenDesktop = Number(swiperEl.dataset.spaceBetweenDesktop) || 0
    const spaceBetweenTablet = Number(swiperEl.dataset.spaceBetweenTablet) || spaceBetweenDesktop

    // Get layout settings
    const mobileLayout = swiperEl.dataset.mobileLayout || 'slider'
    const desktopLayout = swiperEl.dataset.desktopLayout || 'slider'

    // Setup media queries
    const mobile = window.matchMedia('(min-width: 0px) and (max-width: 1024px)')
    const desktop = window.matchMedia('(min-width: 1025px)')

    // Handle video playback in slides
    const handleVideoPlayback = () => {
      // Using postMessage controls only
      // Pause all videos first
      const videos = swiperEl.querySelectorAll('video')
      videos.forEach(video => {
        if (!video.classList.contains('section__wrapper-background')) {
          video.pause()
        }
      })

      // Determine visible vs hidden/active slides
      const allSlides = Array.from(swiperEl.querySelectorAll('swiper-slide'))
      const visibleSlides = allSlides.filter(
        slide => slide.classList.contains('swiper-slide-visible')
      )
      const nonActiveSlides = allSlides.filter(
        slide => !slide.classList.contains('swiper-slide-active')
      )

      // Pause external videos (YouTube/Vimeo) in all non-active slides
      nonActiveSlides.forEach(slide => {
        const type = (slide.getAttribute('data-type') || '').toLowerCase()
        const iframe = slide.querySelector('iframe')
        if (!iframe) return

        //this seems to work better than the postMessage approach
        if (iframe.tagName.toLowerCase() === "video") {
          iframe.pause()
        } else {
          var src = iframe.src
          iframe.src = src
        }

        try {
          if (type === 'youtube') {
            iframe.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');

          } else if (type === 'vimeo') {
            iframe.contentWindow.postMessage('{"method":"pause"}', '*');
          }
        } catch (e) {
          // Swallow cross-origin or timing issues silently
        }
      })

      // Play videos in visible slides
      visibleSlides.forEach(slide => {
        const video = slide.querySelector('video')
        if (video && video.hasAttribute('autoplay')) {
          video.play()
        }
      })
    }

    // Handle text pagination updates
    const updateTextPagination = () => {
      const parentContainer = swiperEl.closest('.slideshow-text-controls__wrapper')
      if (!parentContainer) return

      const activeSlide = swiperEl.swiper.slides[swiperEl.swiper.realIndex]
      if (!activeSlide) return

      const activeSlideId = activeSlide.getAttribute('data-slide')
      const textItems = parentContainer.querySelectorAll('.slideshow-text-controls__item')

      textItems.forEach(item => {
        item.classList.toggle('active', item.getAttribute('data-slide') === activeSlideId)
      })
    }

    // Initialize thumbnails navigation
    const initThumbnails = () => {
      const thumbnailsContainer = swiperEl.closest('.main-product__media-wrapper')?.querySelector('.swiper-thumbnails .swiper')
      if (!thumbnailsContainer || thumbnailsContainer === swiperEl) return null

      const thumbnailsParams = {
        direction: 'vertical',
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchSlidesProgress: true,
        navigation: {
          nextEl: '.swiper-button-thumbs-next',
          prevEl: '.swiper-button-thumbs-prev',
        },
        breakpoints: {
          0: {
            direction: 'horizontal',
            slidesPerView: 4,
          },
          768: {
            direction: 'vertical',
            slidesPerView: 'auto',
          }
        }
      }

      if (!this.initializedSwipers.has(thumbnailsContainer)) {
        this.initializedSwipers.add(thumbnailsContainer)
        Object.assign(thumbnailsContainer, thumbnailsParams)
        thumbnailsContainer.initialize()
      } else if (thumbnailsContainer.swiper) {
        thumbnailsContainer.swiper.update()
      }

      return thumbnailsContainer.swiper || null
    }

    const scrollThumbnailsByDirection = (thumbnailSwiper, direction) => {
      if (!thumbnailSwiper || thumbnailSwiper.destroyed || !direction) return

      const nextIndex = direction === 'next'
        ? thumbnailSwiper.activeIndex + 1
        : thumbnailSwiper.activeIndex - 1

      thumbnailSwiper.slideTo(Math.max(0, Math.min(nextIndex, thumbnailSwiper.slides.length - 1)))
    }

    const setupThumbnailClickAutoScroll = (thumbnailSwiper) => {
      const thumbnailsElement = thumbnailSwiper?.el?.getRootNode()?.host || thumbnailSwiper?.el
      if (!thumbnailsElement || thumbnailsElement._thumbnailClickAutoScrollSetup) return

      let pendingDirection = null

      thumbnailsElement._thumbnailClickAutoScrollSetup = true
      thumbnailsElement.querySelectorAll('swiper-slide').forEach(slide => {
        slide.addEventListener('pointerdown', () => {
          const clickedIndex = Array.from(thumbnailsElement.querySelectorAll('swiper-slide')).indexOf(slide)
          const currentMainIndex = swiperEl.swiper?.realIndex ?? swiperEl.swiper?.activeIndex

          pendingDirection = null

          if (clickedIndex === -1 || currentMainIndex === undefined || clickedIndex === currentMainIndex) return

          pendingDirection = clickedIndex > currentMainIndex ? 'next' : 'prev'
        })
      })

      thumbnailsElement.addEventListener('click', event => {
        const clickedSlide = event.composedPath().find(node => node instanceof Element && node.matches('swiper-slide'))
        if (!clickedSlide || !thumbnailsElement.contains(clickedSlide)) return
        if (!pendingDirection) return

        requestAnimationFrame(() => {
          scrollThumbnailsByDirection(thumbnailSwiper, pendingDirection)
          pendingDirection = null
        })
      }, true)
    }

    const syncThumbnailsWithMain = (thumbnailSwiper) => {
      if (!thumbnailSwiper || thumbnailSwiper.destroyed || !swiperEl.swiper) return

      thumbnailSwiper.update()

      const activeIndex = swiperEl.swiper.realIndex ?? swiperEl.swiper.activeIndex
      const visibleIndexes = thumbnailSwiper.visibleSlidesIndexes || []
      if (!visibleIndexes.length || visibleIndexes.includes(activeIndex)) return

      const firstVisibleIndex = visibleIndexes[0]
      const lastVisibleIndex = visibleIndexes[visibleIndexes.length - 1]

      if (activeIndex > lastVisibleIndex) {
        scrollThumbnailsByDirection(thumbnailSwiper, 'next')
      } else if (activeIndex < firstVisibleIndex) {
        scrollThumbnailsByDirection(thumbnailSwiper, 'prev')
      }
    }

    // Setup text pagination click handlers
    const setupTextPaginationControls = () => {
      if (!hasTextPagination) return

      const parentContainer = swiperEl.closest('.slideshow-text-controls__wrapper')
      if (!parentContainer) return

      const textItems = parentContainer.querySelectorAll('.slideshow-text-controls__item')
      textItems.forEach(item => {
        item.addEventListener('click', () => {
          const slideId = item.getAttribute('data-slide')
          const slideIndex = Array.from(swiperEl.swiper.slides).findIndex(
            slide => slide.getAttribute('data-slide') === slideId
          )
          if (slideIndex !== -1) {
            swiperEl.swiper.slideTo(slideIndex)
          }
        })
      })
    }

    // Handle layout changes
    const buildSlider = () => {
      const shouldBeSlider = (desktop.matches && desktopLayout === 'slider') ||
                             (mobile.matches && mobileLayout === 'slider')

      if (shouldBeSlider) {
        if (!swiperEl.swiper || swiperEl.swiper.destroyed) {
          const thumbnailSwiper = initThumbnails()

          // Get initial values from data attributes
          const mobileSlidesPerView = parseFloat(swiperEl.dataset.mobileSlidesPerView) || 'auto'
          const desktopSlidesPerView = parseFloat(swiperEl.dataset.desktopSlidesPerView) || 'auto'
          const hasTabletSlidesPerView = swiperEl.dataset.tabletSlidesPerView !== undefined
          const tabletSlidesPerView = hasTabletSlidesPerView
            ? (parseFloat(swiperEl.dataset.tabletSlidesPerView) || 'auto')
            : null

          const breakpoints = hasTabletSlidesPerView
            ? {
                0: {
                  slidesPerView: mobileSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenMobile)
                },
                1025: {
                  slidesPerView: tabletSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenTablet),
                },
                1440: {
                  slidesPerView: desktopSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenDesktop),
                }
              }
            : {
                0: {
                  slidesPerView: mobileSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenMobile)
                },
                1025: {
                  slidesPerView: desktopSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenDesktop),
                }
              }

          // Basic swiper parameters
          const params = {
            ...(swiperEl.closest('.card__image') ? {
              virtual: {
                enabled: true,
              },
              nested: true,
            } : {}),
            breakpoints,
            ...(thumbnailSwiper ? {
              thumbs: {
                swiper: thumbnailSwiper
              }
            } : {})
          }

          // Set parameters
          Object.assign(swiperEl, params)

          // Add event listeners before initialization
          swiperEl.addEventListener('swiperafterinit', () => {
            const parentSection = swiperEl.closest('.shopify-section') || swiperEl.closest('.main-product__media')

            if (parentSection) {
              parentSection.classList.add('slider-enabled')
            }
          })

          // Listen for slide changes
          swiperEl.addEventListener('swiperslidechange', () => {
            // Defer to ensure Swiper has updated slide classes (active/visible)
            setTimeout(() => {
              handleVideoPlayback()
              updateTextPagination()
              syncThumbnailsWithMain(thumbnailSwiper)
              // Re-run image animations so active/visible slide images receive `image-loaded`
              reanimateImages(swiperEl)
            }, 0)
          })

          // Listen for swiper destruction
          swiperEl.addEventListener('swiperdestroy', () => {
            const parentSection = swiperEl.closest('.shopify-section')
            if (parentSection) {
              parentSection.classList.remove('slider-enabled')
            }
            // Clean up resize listener
            if (swiperEl._resizeHandler) {
              window.removeEventListener('resize', swiperEl._resizeHandler)
              swiperEl._resizeHandler = null
            }
          })

          // Initialize swiper after setting up event listeners
          swiperEl.initialize()
          setupThumbnailClickAutoScroll(thumbnailSwiper)
          if (hasTextPagination) {
            setupTextPaginationControls()
            updateTextPagination()
          }
        } else {
          swiperEl.swiper.update()
        }
      } else {
        // Initialize empty swiper
        Object.assign(swiperEl)
        swiperEl.initialize()
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true)
          const parentSection = swiperEl.closest('.shopify-section')
          if (parentSection) {
            parentSection.classList.remove('slider-enabled')
          }
        }
      }

      // Layout is now resolved (grid `::part(wrapper)` applied, or slider built).
      // Reveal the container to avoid the pre-init full-width flash. Idempotent on resize.
      swiperEl.classList.add('is-ready')
    }

    // Store resize handler reference for cleanup
    swiperEl._resizeHandler = buildSlider
    window.addEventListener('resize', swiperEl._resizeHandler)

    // Initial check
    buildSlider()
  }
}

// Initialize immediately for better LCP performance
const swiperInit = new SwiperInit()

// Export a function to manually trigger re-initialization when new swipers are added to the DOM
export const reinitializeSwipers = () => {
  swiperInit.initializeSwipers()
}

